/* eslint-disable react-hooks/exhaustive-deps */
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EXECUTE_DEPOSIT } from '@comeon/actions-public';

import { selectCurrency, selectPlayerId } from '~/components/account/ducks';
import {
	IsGermanRegulatedFranchise,
	selectIsNewCashierQDFlow,
	selectSwishQRCodeFlow
} from '~/components/brand/ducks/selectors';
import { currentGameSelector } from '~/components/casino-shared/game-loader/ducks/selectors';
import { getRGMessage } from '~/components/payment/common/lib/utils/constants';
import SchufaConsentPopup from '~/components/payment/deposit/components/schufa-popup-cashier';
import { getCVVBypass } from '~/components/payment/ducks/payment';
import { selectDepositType } from '~/components/payment/ducks/quick-deposit-duck';
import { loadPaypalScriptAsync } from '~/components/payment/hooks/use-paypal-order-ref';
import useSeonPayment from '~/components/payment/hooks/useSeonPayment';
import { textKeysNewQDAmountContainer } from '~/components/payment/quick-deposit-beta/components-v2/constants';
import NewQDCVVscreen from '~/components/payment/quick-deposit-beta/components-v2/cvv-modal';
import Header from '~/components/payment/quick-deposit-beta/components-v2/header';
import NewQDMethodsCollapse from '~/components/payment/quick-deposit-beta/components-v2/methods';
import QDPaypalscreen from '~/components/payment/quick-deposit-beta/components/qd-paypal-modal';
import {
	ExtraMethodParams,
	InstantDepositData,
	InstantDepositInfoForMethods,
	ProviderLimitsData,
	Result,
	SelectedMethod,
	TAction,
	TriggerViewFn
} from '~/components/payment/quick-deposit-beta/types';
import {
	API_LOADING,
	CREDITCARD,
	DEPOSIT_REDIRECT_CONFIRM,
	LOADING,
	MAIN_CONTENT,
	PAYPAL,
	PROMPT_CVV,
	PROMPT_ONECLICK,
	QD_EXECUTE,
	SHUFA_CONSENT,
	SWISH,
	textKeysQDAmountScreen
} from '~/components/payment/quick-deposit-beta/utils/constants';
import executeInstantDeposit, { checkSchufaConsent } from '~/components/payment/quick-deposit-beta/utils/execute-util';
import { getLogo, renderAccountDetails } from '~/components/payment/quick-deposit-beta/utils/qd-helpers';
import { Spinner } from '~/components/payment/quick-deposit-beta/utils/qd-styled-utils';
import { useQDNotifications } from '~/components/payment/quick-deposit-beta/utils/use-quick-deposit-hooks';
import useTranslations from '~/components/translation/use-translations';

import {
	ActionButtonsWrapper,
	AmountInputWrapper,
	ContentWrapper,
	LimitInfoText,
	PaymentButtonSecondary,
	RoundedButtonSecondary,
	StyledAmountInput,
	SubTitle
} from './styles';

type Props = {
	addView: (...args: string[]) => TAction;
	hideView: (...args: string[]) => TAction;
	instantDepositData: InstantDepositData;
	providerLimitsData: ProviderLimitsData;
	redirectToPayments: () => void;
	selectedMethod: SelectedMethod;
	setCrossLimit: (value: boolean) => void;
	setRGMessageState: (value: boolean) => void;
	setSelectedMethod: (provider: InstantDepositInfoForMethods) => void;
	setShowKYCMessage: (value: boolean) => void;
	setSchufaError: (value: string) => void;
	setLugasLimit: (value: boolean) => void;
	updateExecuteResult: (value: Result) => void;
	updateAmountVal: number;
	updateViews: (...args: ReturnType<TriggerViewFn>[]) => TAction;
	views: string[];
};

const NewQDAmountContainer = ({
	addView,
	hideView,
	instantDepositData: { depositInfo, depositInfoStatus },
	providerLimitsData,
	redirectToPayments,
	selectedMethod,
	setCrossLimit,
	setRGMessageState,
	setSelectedMethod,
	setSchufaError,
	setLugasLimit,
	setShowKYCMessage,
	updateExecuteResult,
	updateAmountVal,
	updateViews,
	views
}: Props) => {
	const dispatch = useDispatch();

	const [prompt, setPrompt] = useState<string | null>('');
	const [amount, setAmount] = useState(0);
	const [limitInfo, setLimitInfo] = useState('');
	const [showDepositMaxButton, setShowDepositMaxButton] = useState(false);
	const [disableSubmit, setDisableStatus] = useState(false);
	const [methodListToggle, toggleMethodList] = useState(false);
	const [executeOnMethods, setExecuteOnMethodsFlag] = useState(false);

	const { showErrorNotification } = useQDNotifications();
	const currency = useSelector(selectCurrency) as string | undefined;
	const playerId = useSelector(selectPlayerId) as string | undefined;
	const isNewCashierQD = useSelector(selectIsNewCashierQDFlow);
	const swishQRCodeFlow = useSelector(selectSwishQRCodeFlow);
	const isGermanFranchise = useSelector(IsGermanRegulatedFranchise);

	let bypassCvv: boolean;

	const hasCasinoGame = !!useSelector(currentGameSelector);

	const depositType = useSelector(selectDepositType);

	const selectedAccountDetails = renderAccountDetails(selectedMethod, 'button');
	const { isLoadingTranslations, translations: t } = useTranslations([
		...textKeysQDAmountScreen,
		...textKeysNewQDAmountContainer
	]);
	const { addSeonEvent } = useSeonPayment();
	const {
		sum_limit: sumLimit,
		sum_min: sumMin,
		sum_max: sumMax,
		formattedSumMin,
		formattedSumMax,
		paymentMethodId,
		providerId,
		responsibleGamingLimitType
	} = providerLimitsData;

	const checkLimitsStatus = (amountValue: number): boolean => {
		if (sumLimit && amountValue > sumLimit) {
			setCrossLimit(true);
			setRGMessageState(true);
		}

		switch (true) {
			case amountValue < sumMin:
				setLimitInfo(`${t.AMOUNT_MIN} ${formattedSumMin}`);
				setDisableStatus(true);

				return false;

			case amountValue > sumMax:
				setLimitInfo(`${t[getRGMessage(responsibleGamingLimitType)]} ${formattedSumMax}`);
				setDisableStatus(true);
				setShowDepositMaxButton(true);

				return false;

			default:
				setLimitInfo('');
				setDisableStatus(false);
				setShowDepositMaxButton(false);

				return true;
		}
	};

	const validateAndSetAmount = (value: number) => {
		setAmount(value);
		checkLimitsStatus(value);
	};

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		const inputVal = event.target?.value;
		const formattedVal = Number(inputVal);

		if (!inputVal) setDisableStatus(true);

		if (!Number.isNaN(formattedVal)) validateAndSetAmount(formattedVal);
	};

	const updateViewOnAmount = (inputVal: number) => {
		switch (true) {
			case inputVal < sumMin:
				setRGMessageState(true);
				break;

			case sumMax < sumMin:
				setRGMessageState(true);
				break;

			case inputVal > sumMax:
				validateAndSetAmount(sumMax);
				break;

			default:
				validateAndSetAmount(inputVal);
				break;
		}
	};

	const selectMethodAndExecute = (provider: InstantDepositInfoForMethods) => {
		setSelectedMethod(provider);
		setExecuteOnMethodsFlag(true);
		toggleMethodList(!methodListToggle);
	};

	const executeQDDeposit = async (extraMethodParams: ExtraMethodParams) => {
		updateViews(addView(LOADING));

		await addSeonEvent();

		const result = (await executeInstantDeposit(
			{
				...selectedMethod,
				...extraMethodParams,
				amount,
				depositType
			},
			dispatch
		)) as Result;

		if (result.successful) {
			if (isNewCashierQD) {
				updateExecuteResult(result);
			} else {
				dispatch({
					type: EXECUTE_DEPOSIT,
					payload: {
						result,
						spec: { exe_pending: false }
					}
				});
				updateViews(hideView(MAIN_CONTENT, LOADING));
			}

			updateViews(addView(QD_EXECUTE));
		} else {
			setPrompt(null);
			updateViews(hideView(LOADING));
			const { availableDepositAmount } = result;
			if (typeof availableDepositAmount !== 'undefined' && availableDepositAmount !== null) {
				setLugasLimit(true);
				return setRGMessageState(true);
			}
			const errorMessage = result.errorMessage
				? `${result.errorMessage.title} - ${result.errorMessage.message}`
				: t.SYSTEM_ERROR_GENERAL;
			showErrorNotification(errorMessage);
		}
	};

	const getExtraParamsFromComponent = async () => {
		const { extraParameters, CVV2Required, playerCardData } = selectedMethod;
		const { promptModalCode, activePaypalBillingAgreementTokenId } = extraParameters;
		const oneClickDisabled = !activePaypalBillingAgreementTokenId;

		switch (selectedMethod.paymentMethodName) {
			case SWISH:
				return { params: { swishQRCodeFlow }, shouldExecuteDeposit: true };

			case CREDITCARD:
				// Check for CVV2 prompt condition before proceeding with execute
				if (CVV2Required) {
					updateViews(addView(LOADING));
					bypassCvv = (await getCVVBypass(
						dispatch,
						playerCardData.id,
						amount,
						providerId,
						paymentMethodId
					)) as boolean;
					updateViews(hideView(LOADING));
					if (!bypassCvv) {
						setPrompt(PROMPT_CVV as string);
						return { params: {}, shouldExecuteDeposit: false };
					}
				}

				return { params: { bypassCvv }, shouldExecuteDeposit: true };

			case PAYPAL: {
				// Check for PayPal prompt condition before proceeding with execute
				if (promptModalCode === PROMPT_ONECLICK && oneClickDisabled) {
					setPrompt(PROMPT_ONECLICK as string);
					return { params: {}, shouldExecuteDeposit: false };
				}

				/** extraParams for PAYPAL is set here incase of OneClickEnabled,
				 * oneClickDisabled flow will set these values in QDPaypalscreen */
				updateViews(addView(LOADING));
				const OrderFuniquedID = await loadPaypalScriptAsync();
				return { params: { enableOneClick: true, OrderFuniquedID, cu: currency }, shouldExecuteDeposit: true };
			}

			default:
				return { params: {}, shouldExecuteDeposit: true };
		}
	};

	const initiateDeposit = async () => {
		const { params, shouldExecuteDeposit } = await getExtraParamsFromComponent();

		if (shouldExecuteDeposit) {
			return executeQDDeposit(params);
		}
	};

	const handleDepositExecution = async () => {
		const { needValidation } = selectedMethod.depositKycData || {};

		if (needValidation) {
			return setShowKYCMessage(true);
		}

		if (isGermanFranchise) {
			updateViews(addView(LOADING));

			const schufaConsentNeeded = await checkSchufaConsent(amount, playerId);
			updateViews(hideView(LOADING));
			if (schufaConsentNeeded) {
				return setPrompt(SHUFA_CONSENT as string);
			}
		}

		return initiateDeposit();
	};

	useEffect(() => {
		if (isLoadingTranslations || depositInfoStatus === API_LOADING) {
			updateViews(addView(LOADING));
		} else {
			updateViews(hideView(LOADING));
		}
	}, [isLoadingTranslations]);

	useEffect(() => {
		if (!isLoadingTranslations) {
			setLimitInfo(t.EDIT_AMOUNT);
		}
	}, [isLoadingTranslations, t]);

	useEffect(() => {
		if (updateAmountVal !== Number(amount) && Object.keys(providerLimitsData).length && !executeOnMethods) {
			const val = updateAmountVal < sumMin ? sumMin : updateAmountVal;

			updateViewOnAmount(val);
		}

		if (executeOnMethods && checkLimitsStatus(amount)) {
			handleDepositExecution().catch(error =>
				// eslint-disable-next-line no-console
				console.error('quick-deposit-amount-screen => handleExecuteDeposit ~ 335 line', error)
			);
		}
	}, [updateAmountVal, providerLimitsData]);

	const handleMoreMethodsClick = useCallback(() => {
		if (hasCasinoGame) {
			updateViews(addView(DEPOSIT_REDIRECT_CONFIRM));
		} else {
			redirectToPayments();
		}
	}, [hasCasinoGame]);

	const handleSetDepositMaxAmount = useCallback(() => {
		if (sumMax) {
			validateAndSetAmount(sumMax);
			setShowDepositMaxButton(false);
			setLimitInfo('');
			setDisableStatus(false);
		}
	}, [sumMax]);

	const renderContent = () => {
		const amountValue = amount === 0 ? '' : amount.toString();

		switch (prompt) {
			case PROMPT_CVV:
				// eslint-disable-next-line @typescript-eslint/no-misused-promises
				return <NewQDCVVscreen views={views} translation={t} executeQDDeposit={executeQDDeposit} />;

			case PROMPT_ONECLICK:
				return <QDPaypalscreen views={views} translation={t} executeQDDeposit={executeQDDeposit} />;

			case SHUFA_CONSENT:
				return (
					<SchufaConsentPopup
						onSuccess={initiateDeposit}
						amount={amount}
						status={{ methodId: selectedMethod?.depositMethodId, sum_min: sumMin }}
						onReject={() => setPrompt(null)}
						setSchufaError={setSchufaError}
						qdDepositFlow
					/>
				);

			default:
				return (
					<>
						<AmountInputWrapper>
							<StyledAmountInput
								id="amountInput"
								automationClass=""
								className="button u-mb2"
								data-map="phone"
								disabled={views.includes(LOADING)}
								hasError={!views.includes(LOADING) && !!limitInfo}
								onChange={handleChange}
								placeholder={t.NEW_QUICK_DEPOSIT_ENTER_AMOUNT}
								type="tel"
								value={amountValue}
							/>
							{!!limitInfo && (
								<LimitInfoText disabled={views.includes(LOADING)}>{limitInfo}</LimitInfoText>
							)}
						</AmountInputWrapper>
						<ActionButtonsWrapper>
							{showDepositMaxButton && (
								<RoundedButtonSecondary
									smallfont
									onClick={handleSetDepositMaxAmount}
									type="submit"
									className="button u-mb1 u-mt1"
									title="title"
									dataAt="new-mobile-quick-deposit-button"
								>
									{t.MOBILE_DEPOSIT_MAX_TEXT}
								</RoundedButtonSecondary>
							)}
							<PaymentButtonSecondary
								smallfont
								disabled={disableSubmit || views.includes(LOADING)}
								// eslint-disable-next-line @typescript-eslint/no-misused-promises
								onClick={handleDepositExecution}
								type="submit"
								className="button u-mb1 u-mt1"
								title="title"
								dataAt="new-mobile-quick-deposit-button"
							>
								{t.NEW_QUICK_DEPOSIT_DEPOSIT_BUTTON}
								{selectedMethod && getLogo(selectedMethod, true, true)}
								{selectedAccountDetails?.accountInfo}
							</PaymentButtonSecondary>
						</ActionButtonsWrapper>
						<NewQDMethodsCollapse
							depositInfo={depositInfo}
							methodListToggle={methodListToggle}
							onMoreMethodsClick={handleMoreMethodsClick}
							selectMethodAndExecute={selectMethodAndExecute}
							selectedMethod={selectedMethod}
							toggleMethodList={toggleMethodList}
							views={views}
						/>
					</>
				);
		}
	};

	return (
		<>
			<Header textHeading={prompt === PROMPT_CVV ? t.QD_CVV_INPUT : undefined} />
			<ContentWrapper>
				{prompt === PROMPT_CVV && <SubTitle dataAt="sub-title-upload-documents">{t.QD_CVV_INFO}</SubTitle>}
				{renderContent()}
				{views.includes(LOADING) && <Spinner key={LOADING} className="spinner" />}
			</ContentWrapper>
		</>
	);
};

export default NewQDAmountContainer;
